'use client'

import { memo, useEffect, useState, useRef } from 'react'
import { css } from '@emotion/react'
import {
  Box,
  Flex,
  useColorModeValue,
  useBreakpointValue,
  IconButton,
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerBody,
  useDisclosure,
} from '@chakra-ui/react'
import { Menu } from 'lucide-react'
import PublicHeader from './PublicHeader'
import useResponsive from '../../utils/hooks/useResponsive'
import PublicListSearchContent from './PublicListSearchContent'
import { useGetPublicBookmarksFolder } from '../../services/public'
import { toastError } from '../../constants'
import FolderTree from './FolderTree'
import FolderBreadcrumb from './FolderBreadcrumb'

const styles = {
  container: css`
    height: 100%;
    overflow: hidden;
    display: flex;
    flex-direction: column;
  `,
  contentContainer: css`
    flex: 1;
    overflow: hidden;
    display: flex;
  `,
  mainContent: publicHeaderHeight => css`
    height: calc(100% - ${publicHeaderHeight}px);
    display: flex;
    flex-direction: column;
    flex: 1;
    overflow: hidden;
  `,
  folderTreeContainer: (isMobile, padding) => css`
    padding: ${padding};
    width: ${isMobile ? '100%' : '280px'};
    height: 100%;
    overflow-y: auto;
    flex-shrink: 0;
  `,
  bookmarkContainer: css`
    flex: 1;
    overflow: hidden;
    display: flex;
    flex-direction: column;
  `,
  mobileMenuButton: css`
    display: flex;
    align-items: center;
    justify-content: center;
  `,
}

function PublicFolderContent({ userId, folderId, folderAndCount }) {
  const [activeFolderId, setActiveFolderId] = useState(folderId)
  const [bookmarkCounts, setBookmarkCounts] = useState({})
  const [folderPath, setFolderPath] = useState([])

  // Responsive layout
  const isMobile = useBreakpointValue({ base: true, md: false })
  const { isOpen, onOpen, onClose } = useDisclosure()
  const btnRef = useRef()

  // Get bookmarks for the active folder
  const { bookmarks, isErrorBookmarks } = useGetPublicBookmarksFolder(userId, activeFolderId)

  useEffect(() => {
    if (isErrorBookmarks) {
      toastError('bookmarks')
    }
  }, [isErrorBookmarks])

  // Build folder path for breadcrumbs
  useEffect(() => {
    const buildFolderPath = () => {
      // If active folder is root folder
      if (activeFolderId === folderAndCount.folder._id) {
        setFolderPath([{ id: folderAndCount.folder._id, name: folderAndCount.folder.name }])
        return
      }

      // Find the active folder and its ancestors
      const folderMap = new Map()
      folderMap.set(folderAndCount.folder._id.toString(), {
        id: folderAndCount.folder._id,
        name: folderAndCount.folder.name,
        parent: null,
      })

      // Add all child folders to map
      folderAndCount.childFolders.forEach(folder => {
        folderMap.set(folder._id.toString(), {
          id: folder._id,
          name: folder.name,
          parent: folder.parent?.toString(),
        })
      })

      // Build path from active folder to root
      const path = []
      let currentId = activeFolderId.toString()

      while (currentId) {
        const folder = folderMap.get(currentId)
        if (!folder) break

        path.unshift(folder)
        currentId = folder.parent
      }

      setFolderPath(path)
    }

    buildFolderPath()
  }, [activeFolderId, folderAndCount])

  const padding = useResponsive('10px', '16px')
  const publicHeaderHeight = useResponsive(46, 53)

  const handleFolderSelect = folderId => {
    setActiveFolderId(folderId)
    if (isMobile) {
      onClose()
    }
  }

  // Color mode values
  const borderColor = useColorModeValue('gray.200', 'gray.700')
  const folderTreeBg = useColorModeValue('gray.50', 'gray.900')

  return (
    <Box css={styles.container}>
      <PublicHeader userId={userId} headerHeight={publicHeaderHeight} padding={padding} />

      <Box css={styles.contentContainer}>
        {/* Folder Tree - Desktop */}
        {!isMobile && (
          <Box
            css={styles.folderTreeContainer(false, padding)}
            borderRightWidth="1px"
            borderColor={borderColor}
            bg={folderTreeBg}
          >
            <FolderTree
              rootFolder={folderAndCount.folder}
              childFolders={folderAndCount.childFolders}
              activeFolderId={activeFolderId}
              onFolderSelect={handleFolderSelect}
              bookmarkCounts={bookmarkCounts}
            />
          </Box>
        )}

        {/* Main Content */}
        <Box css={styles.mainContent(publicHeaderHeight)}>
          {/* Mobile Menu Button */}
          {isMobile && (
            <Flex p={padding} borderBottomWidth="1px" borderColor={borderColor}>
              <IconButton
                ref={btnRef}
                icon={<Menu />}
                aria-label="Open folder menu"
                variant="outline"
                size="sm"
                onClick={onOpen}
                mr={2}
              />
              <FolderBreadcrumb folderPath={folderPath} onFolderSelect={handleFolderSelect} />
            </Flex>
          )}

          {/* Breadcrumb - Desktop */}
          {!isMobile && (
            <Box p={padding} borderBottomWidth="1px" borderColor={borderColor}>
              <FolderBreadcrumb folderPath={folderPath} onFolderSelect={handleFolderSelect} />
            </Box>
          )}

          {/* Bookmark Content */}
          <Box css={styles.bookmarkContainer}>
            <PublicListSearchContent
              userId={userId}
              folderId={activeFolderId}
              padding={padding}
              bookmarks={bookmarks}
              publicHeaderHeight={0} // We're handling the header separately now
              isErrorBookmarks={isErrorBookmarks}
              folderAndCount={folderAndCount}
            />
          </Box>
        </Box>

        {/* Mobile Drawer for Folder Tree */}
        {isMobile && (
          <Drawer isOpen={isOpen} placement="left" onClose={onClose} finalFocusRef={btnRef}>
            <DrawerOverlay />
            <DrawerContent>
              <DrawerBody p={0}>
                <Box css={styles.folderTreeContainer(true, padding)} pt={4}>
                  <FolderTree
                    rootFolder={folderAndCount.folder}
                    childFolders={folderAndCount.childFolders}
                    activeFolderId={activeFolderId}
                    onFolderSelect={handleFolderSelect}
                    bookmarkCounts={bookmarkCounts}
                  />
                </Box>
              </DrawerBody>
            </DrawerContent>
          </Drawer>
        )}
      </Box>
    </Box>
  )
}

export default memo(PublicFolderContent)
